import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiStar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { settingsAPI } from '../../services/api';
import useScrollAnimation from '../../hooks/useScrollAnimation';

const fallbackTestimonials = [
  { _id: '1', name: 'Anjali Sharma',  role: 'Parent of Grade 8 Student', rating: 5,
    text: "Hillbert English Boarding School  has transformed my daughter's approach to learning. The teachers are incredibly dedicated and the school's focus on holistic development has helped her grow not just academically but as a person." },
  { _id: '2', name: 'Rohan Thapa',    role: 'Alumni, Class of 2022', rating: 5,
    text: 'My years at Hillbert English Boarding School  were the foundation of my success. The rigorous academic program, combined with extracurricular activities, prepared me well for university.' },
  { _id: '3', name: 'Priya Shrestha', role: 'Parent of Grade 5 Student', rating: 5,
    text: 'The quality of education here is outstanding. My son loves going to school every day, which says a lot about the teaching methods and the positive environment the teachers have created.' },
];

const getInitials = (name) =>
  name ? name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) : '??';

const StarRating = ({ rating }) => (
  <div className="flex gap-1">
    {[1,2,3,4,5].map((s) => (
      <FiStar key={s} className={`w-4 h-4 ${s <= rating ? 'text-[#2563EB] fill-[#2563EB]' : 'text-gray-200'}`} />
    ))}
  </div>
);

const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-1 rounded-full bg-[#2563EB]" />
    <span className="text-[#2563EB] text-xs font-bold uppercase tracking-[0.25em]">{text}</span>
  </div>
);

export default function TestimonialsSection() {
  const [testimonials, setTestimonials] = useState(fallbackTestimonials);
  const [current, setCurrent] = useState(0);
  const { ref, isVisible } = useScrollAnimation();

  useEffect(() => {
    settingsAPI.get().then((res) => {
      const data = res.data?.data?.settings?.testimonials || [];
      if (data.length > 0) setTestimonials(data);
    }).catch(() => {});
  }, []);

  useEffect(() => { setCurrent(0); }, [testimonials.length]);
  useEffect(() => {
    if (testimonials.length <= 1) return;
    const t = setInterval(() => setCurrent((p) => (p + 1) % testimonials.length), 5000);
    return () => clearInterval(t);
  }, [testimonials.length]);

  const prev = () => setCurrent((c) => (c - 1 + testimonials.length) % testimonials.length);
  const next = () => setCurrent((c) => (c + 1) % testimonials.length);
  const item = testimonials[current] || testimonials[0];
  if (!item) return null;

  return (
    <section className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
        <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={isVisible ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="mb-16">
          <SectionLabel text="Testimonials" />
          <h2 className="text-4xl sm:text-5xl font-bold text-[#0F172A] leading-tight">
            What People <span className="text-[#2563EB]">Say</span>
          </h2>
        </motion.div>

        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div key={current}
              initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.35 }}
              className="bg-[#F0F4FF] border border-blue-100 rounded-3xl p-8 sm:p-12 lg:p-16 relative">
              <div className="absolute top-8 right-10 text-8xl font-serif font-black text-[#2563EB]/10 leading-none select-none">"</div>
              <div className="relative z-10">
                <StarRating rating={item.rating || 5} />
                <blockquote className="text-slate-600 text-xl sm:text-2xl leading-relaxed my-8 font-medium">
                  "{item.text}"
                </blockquote>
                <div className="flex items-center gap-5 mt-8">
                  <div className="w-14 h-14 bg-[#2563EB] rounded-2xl flex items-center justify-center text-white font-black font-mono text-lg shadow-md">
                    {getInitials(item.name)}
                  </div>
                  <div>
                    <p className="text-[#0F172A] font-bold text-base">{item.name}</p>
                    <p className="text-[#0EA5E9] text-xs font-bold uppercase tracking-widest mt-1">{item.role}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {testimonials.length > 1 && (
            <div className="flex items-center gap-4 mt-8">
              <button onClick={prev} className="w-11 h-11 bg-blue-50 hover:bg-[#2563EB] text-slate-500 hover:text-white flex items-center justify-center transition-all rounded-xl shadow-sm">
                <FiChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-2 mx-4">
                {testimonials.map((_, idx) => (
                  <button key={idx} onClick={() => setCurrent(idx)}
                    className={`h-2 rounded-full transition-all ${idx === current ? 'bg-[#2563EB] w-8' : 'w-2 bg-blue-200 hover:bg-blue-300'}`} />
                ))}
              </div>
              <button onClick={next} className="w-11 h-11 bg-blue-50 hover:bg-[#2563EB] text-slate-500 hover:text-white flex items-center justify-center transition-all rounded-xl shadow-sm">
                <FiChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
